import { erkenneVerb } from '@spirit-testing/testoffice-ng-engine-core/catalog';
import { ObjectRefSource, ObjectUsages, OpenReference, OwnerKind, UsageLine } from '../../models/object-mapping.model';

/** Aktion einer Zuordnung (Bundle `OB_AKTION_STIL`): Klicken, Eingeben, Prüfen, Bedingung, Sonstiges. */
export type AssignmentAction = 'CLICK' | 'ENTER' | 'CHECK' | 'CONDITION' | 'OTHER';

/** Die Chips der Ansicht nach „Alle“, wie im Bundle `:176`; „Sonstiges“ hat keinen eigenen Chip. */
export const ASSIGNMENT_CHIPS: readonly AssignmentAction[] = ['CLICK', 'ENTER', 'CHECK', 'CONDITION'];

/** Eine Zeile der Zuordnung oder der Warnbox; `object` ist `null`, wenn sie das Testelement selbst anspricht. */
export interface AssignmentRow {
  id: string;
  object: string | null;
  action: AssignmentAction;
  /** Prüfart und Argumente einer Prüfung, sonst leer. */
  expectation: string;
  ownerId: string;
  ownerKey: string;
  ownerTitle: string;
  ownerKind: OwnerKind;
  stepKey: string;
}

const ENTER_VERBS: ReadonlySet<string> = new Set(['EINGABE_IN', 'EINGABE_PASSWORT', 'TEXT_LOESCHEN']);
const CLICK_VERBS: ReadonlySet<string> = new Set([
  'DRUECKE',
  'AUSWAHL_LINK',
  'AUSWAHL_MENUE',
  'AUSWAHL_EINTRAG',
  'ANKREUZEN',
  'KREUZ_ENTFERNEN',
]);

/** Ein Argument: Text in Anfuehrungszeichen, eine Variable oder ein Wort; Kommas und Leerzeichen trennen. */
const ARGUMENT = /"[^"]*"?|\{[^}]*\}?|[^,"{\s]+/gu;
const CHECK_PREFIX = /^IST?\s+/iu;
/** Ab hier beginnt die Bedingung einer Zeile; wie die Engine schneidet die Erwartung dort ab. */
const CONDITION_PART = /\s+(?:WENN|IF)\b.*$/iu;

/** Das Verb erkennt der Parser der Engine selbst (`erkenneVerb`), damit die Ansicht liest, was der Lauf ausfuehrt. */
export function actionOf(source: ObjectRefSource, text: string | null): AssignmentAction {
  if (source === 'TCDL_CONDITION' || source === 'STEP_CONDITION') return 'CONDITION';
  const line = text?.trim();
  if (!line) return 'OTHER';
  const { verb } = erkenneVerb(line);
  if (verb === 'IST') return 'CHECK';
  if (ENTER_VERBS.has(verb)) return 'ENTER';
  if (CLICK_VERBS.has(verb)) return 'CLICK';
  return 'OTHER';
}

/** `IST INHALT, mLogin.tFehler, "x"` → `INHALT "x"`: die Prüfart, dann die Argumente ohne das Objekt und ohne `WENN …`. */
export function expectationOf(text: string | null, reference: string): string {
  const line = text?.trim();
  if (!line) return '';
  const recognized = erkenneVerb(line);
  if (recognized.verb !== 'IST') return '';
  const args = line.slice(recognized.treffer.length).replace(CONDITION_PART, '').match(ARGUMENT) ?? [];
  const objectAt = args.findIndex((arg) => arg.toLowerCase() === reference.toLowerCase());
  if (objectAt >= 0) args.splice(objectAt, 1);
  return [recognized.treffer.replace(CHECK_PREFIX, '').trim(), ...args].join(' ');
}

/** Der Objektname aus `{lib}.{name}` oder der Kurzform; `null`, wenn der Verweis das Testelement selbst nennt. */
export function objectOf(reference: string, libraryName: string): string | null {
  const dot = reference.indexOf('.');
  if (dot > 0) return reference.slice(dot + 1);
  return reference.toLowerCase() === libraryName.toLowerCase() ? null : reference;
}

/** Je Zeile eines Testfalls oder Testmoduls eine Zuordnung; freie Schritte zeigt die Ansicht nicht (L4). */
export function assignmentRows(usages: ObjectUsages | null, libraryName: string): AssignmentRow[] {
  if (!usages) return [];
  return [...usages.testCases, ...usages.modules].flatMap((owner) =>
    owner.steps.flatMap((step) =>
      step.lines.map((line, index) => ({
        ...actionAndExpectation(line),
        id: `${owner.ownerKind}:${owner.id}:${step.stepId}:${index}`,
        object: objectOf(line.reference, libraryName),
        ownerId: owner.id,
        ownerKey: owner.key,
        ownerTitle: owner.title,
        ownerKind: owner.ownerKind,
        stepKey: step.stepKey,
      })),
    ),
  );
}

export function openReferenceRows(refs: readonly OpenReference[] | null): AssignmentRow[] {
  return (refs ?? []).map((ref, index) => ({
    ...actionAndExpectation(ref),
    id: `open:${index}`,
    object: ref.entryName,
    ownerId: ref.ownerId,
    ownerKey: ref.ownerKey ?? '',
    ownerTitle: ref.ownerTitle,
    ownerKind: ref.ownerKind,
    stepKey: ref.stepKey ?? '',
  }));
}

function actionAndExpectation(
  line: Pick<UsageLine, 'source' | 'text' | 'reference'>,
): Pick<AssignmentRow, 'action' | 'expectation'> {
  const action = actionOf(line.source, line.text);
  return { action, expectation: action === 'CHECK' ? expectationOf(line.text, line.reference) : '' };
}
